/**
 * ROARDRIVE in the lettering of the cup dash's original logotype: same stroke, same cap height, same
 * rounded outer corners and square counters. R, O and E are the logotype's own outlines (Wikimedia
 * Commons 'Porsche wordmark.svg', public-domain file; the WORD it spelled is the trademark, and that
 * word is gone since 2026-09-10). A, D, I and V are built to the same rules (stem 13.4, bar 11.3,
 * radius 13.4, cap 60.6), so the nine letters read as one alphabet. Unit space: cap height 60.6.
 */
const CAP = 60.615;
const STEM = 13.4, BAR = 11.3, RAD = 13.4, GAP = 15.7;
const O_PATH = 'M 112.107,13.383 L 112.107,13.383 C 112.107,6.048 106.259,0.033 98.908,0.033 C 98.908,0.033 98.908,0.033 98.908,0.033 L 13.533,0.033 C 6.182,0.033 0.000,5.998 0.000,13.416 L 0.000,47.232 C 0.000,54.650 6.182,60.581 13.533,60.581 L 98.741,60.581 C 106.092,60.581 112.107,54.617 112.107,47.182 L 112.107,13.383 z M 98.741,45.043 C 98.741,46.196 97.738,47.265 96.736,47.265 L 15.204,47.265 C 14.368,47.265 13.366,46.513 13.366,45.628 L 13.366,15.220 C 13.366,14.201 14.536,13.349 15.705,13.349 L 96.569,13.366 C 97.738,13.366 98.741,14.418 98.741,15.655 L 98.741,45.043';
const R_PATH = 'M 101.414,39.947 C 108.598,39.697 114.279,33.782 114.279,26.515 L 114.279,13.383 C 114.279,5.981 108.264,0.067 100.913,0.033 L 100.913,0.017 L 0.000,0.000 L 0.000,60.548 L 13.366,60.548 L 13.366,44.625 L 87.547,44.625 C 94.898,44.625 100.913,50.657 100.913,58.075 L 100.913,60.464 L 114.446,60.464 L 114.446,60.297 C 114.446,51.275 109.099,43.489 101.414,39.947 z M 100.913,29.088 L 100.913,29.088 C 100.913,30.224 100.077,31.293 98.908,31.310 L 13.366,31.310 L 13.366,13.333 L 98.574,13.366 C 99.910,13.366 100.913,14.418 100.913,15.672 L 100.913,29.088';
const E_PATH = 'M 117.954,11.361 L 117.954,0.050 L 0.000,0.050 L 0.000,60.615 L 117.954,60.615 L 117.954,49.304 L 13.366,49.304 L 13.366,35.988 L 117.954,35.988 L 117.954,24.677 L 13.366,24.677 L 13.366,11.361 L 117.954,11.361';
const O_W = 112.107, R_W = 114.446, E_W = 117.954;
const A_W = 114, D_W = 112, I_W = STEM, V_W = 114;

type Letter = { w: number; path: Path2D; rule: CanvasFillRule };
let cache: Letter[] | null = null;

function ring(p: Path2D, x0: number, y0: number, x1: number, y1: number, r: number, tl: boolean, tr: boolean, br: boolean, bl: boolean): void {
  // a rounded rectangle, corner by corner: rounded where asked, square elsewhere
  p.moveTo(x0 + (tl ? r : 0), y0);
  p.lineTo(x1 - (tr ? r : 0), y0); if (tr) p.arcTo(x1, y0, x1, y0 + r, r);
  p.lineTo(x1, y1 - (br ? r : 0)); if (br) p.arcTo(x1, y1, x1 - r, y1, r);
  p.lineTo(x0 + (bl ? r : 0), y1); if (bl) p.arcTo(x0, y1, x0, y1 - r, r);
  p.lineTo(x0, y0 + (tl ? r : 0)); if (tl) p.arcTo(x0, y0, x0 + r, y0, r);
  p.closePath();
}
function rect(p: Path2D, x0: number, y0: number, x1: number, y1: number): void { p.rect(x0, y0, x1 - x0, y1 - y0); }

function letters(): Letter[] {
  if (cache) return cache;
  const O = new Path2D(O_PATH), R = new Path2D(R_PATH), E = new Path2D(E_PATH);
  // A: the two stems joined by the rounded top, a square counter, the crossbar at mid height
  const A = new Path2D();
  A.moveTo(0, CAP); A.lineTo(0, RAD); A.arcTo(0, 0, RAD, 0, RAD); A.lineTo(A_W - RAD, 0); A.arcTo(A_W, 0, A_W, RAD, RAD);
  A.lineTo(A_W, CAP); A.lineTo(A_W - STEM, CAP); A.lineTo(A_W - STEM, STEM); A.lineTo(STEM, STEM); A.lineTo(STEM, CAP); A.closePath();
  rect(A, STEM - 0.5, CAP / 2 - BAR / 2, A_W - STEM + 0.5, CAP / 2 + BAR / 2);
  // D: square on the stem side, rounded on the bowl side, square counter
  const D = new Path2D();
  ring(D, 0, 0, D_W, CAP, RAD, false, true, true, false);
  rect(D, STEM, STEM, D_W - STEM, CAP - STEM);
  // I: the stem
  const I = new Path2D(); rect(I, 0, 0, I_W, CAP);
  // V: two diagonals of the stem's thickness meeting on a short flat foot
  const V = new Path2D();
  const foot = 12, half = V_W / 2, run = half - foot / 2;
  const len = Math.hypot(run, CAP), nx = CAP / len, ny = -run / len;   // unit normal of the left edge, pointing in
  const sw = STEM / (CAP / len);                                        // horizontal width of a diagonal stroke
  const apexY = (STEM * ny) + ((half - STEM * nx) / run) * CAP;         // inner edges meet on the centre line
  V.moveTo(0, 0); V.lineTo(sw, 0); V.lineTo(half, apexY); V.lineTo(V_W - sw, 0); V.lineTo(V_W, 0);
  V.lineTo(half + foot / 2, CAP); V.lineTo(half - foot / 2, CAP); V.closePath();
  cache = [
    { w: R_W, path: R, rule: 'evenodd' }, { w: O_W, path: O, rule: 'evenodd' }, { w: A_W, path: A, rule: 'nonzero' },
    { w: R_W, path: R, rule: 'evenodd' }, { w: D_W, path: D, rule: 'evenodd' }, { w: R_W, path: R, rule: 'evenodd' },
    { w: I_W, path: I, rule: 'nonzero' }, { w: V_W, path: V, rule: 'nonzero' }, { w: E_W, path: E, rule: 'evenodd' },
  ];
  return cache;
}

export const ROARDRIVE_WORDMARK_H = CAP;
export const ROARDRIVE_WORDMARK_W = letters().reduce((s, l) => s + l.w, 0) + GAP * 8;

/** Draw ROARDRIVE centred on (cx, cy), `width` wide, in the logotype lettering. Same signature the marque's drawer had. */
export function drawRoardriveWordmark(ctx: CanvasRenderingContext2D, cx: number, cy: number, width: number, fill: string | CanvasGradient, stroke?: string, strokeW = 0): void {
  const k = width / ROARDRIVE_WORDMARK_W;
  ctx.save();
  ctx.translate(cx - width / 2, cy - (CAP * k) / 2);
  ctx.scale(k, k);
  let x = 0;
  for (const l of letters()) {
    ctx.save(); ctx.translate(x, 0);
    ctx.fillStyle = fill; ctx.fill(l.path, l.rule);
    if (stroke && strokeW > 0) { ctx.strokeStyle = stroke; ctx.lineWidth = strokeW / k; ctx.stroke(l.path); }
    ctx.restore();
    x += l.w + GAP;
  }
  ctx.restore();
}
